import dayjs, { Dayjs } from "dayjs";
import jalaliPlugin from "jalali-plugin-dayjs";

dayjs.extend(jalaliPlugin);

export interface DaysLeftResult {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  totalHours: number; // days folded into hours, e.g. 7 days 4h -> 172
  countdown: string; // "hours:minutes:seconds", e.g. "172:22:08"
  message: string;
}

export interface CurrentYears {
  gregorian: number;
  jalali: number;
  hijri: number;
}

const pad = (n: number): string => String(n).padStart(2, "0");

/**
 * Breaks the gap between now and target into days/hours/minutes/seconds,
 * and builds both a "D:HH:MM:SS" countdown string and a readable message.
 */
const buildBreakdown = (target: Dayjs, now: Dayjs): DaysLeftResult => {
  const totalSeconds = Math.max(0, target.diff(now, "second"));

  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const totalHours = days * 24 + hours;

  const countdown = `${totalHours}:${pad(minutes)}:${pad(seconds)}`;
  const message = totalSeconds === 0 ? "That's now!" : `${countdown} left`;

  return { days, hours, minutes, seconds, totalHours, countdown, message };
};

/**
 * Converts a tabular Hijri (Islamic) date to a Gregorian JS Date.
 * Uses the civil (tabular) Islamic calendar algorithm — may differ by
 * a day from moon-sighting-based announcements in some countries.
 */
function hijriToGregorian(
  hijriYear: number,
  hijriMonth: number,
  hijriDay: number
): Date {
  const julianDay =
    Math.floor((11 * hijriYear + 3) / 30) +
    354 * hijriYear +
    30 * hijriMonth -
    Math.floor((hijriMonth - 1) / 2) +
    hijriDay +
    1948440 -
    385;

  const jd = julianDay + 0.5;
  const z = Math.floor(jd);
  const a = Math.floor((z - 1867216.25) / 36524.25);
  const aa = z + 1 + a - Math.floor(a / 4);
  const b = aa + 1524;
  const c = Math.floor((b - 122.1) / 365.25);
  const d = Math.floor(365.25 * c);
  const e = Math.floor((b - d) / 30.6001);

  const day = b - d - Math.floor(30.6001 * e);
  const month = e < 14 ? e - 1 : e - 13;
  const year = month > 2 ? c - 4716 : c - 4715;

  return new Date(year, month - 1, day);
}

/**
 * Computes a full countdown left until a Hijri (Arabic/Islamic) date.
 * If that date already passed this Hijri year, rolls forward to next year
 * so recurring annual events (birthdays, anniversaries) always count down
 * to their next upcoming occurrence instead of going negative.
 * @param hijriYear e.g. 1448
 * @param hijriMonth 1-12
 * @param hijriDay 1-30
 */
export function getDaysLeftFromHijri(
  hijriYear: number,
  hijriMonth: number,
  hijriDay: number
): DaysLeftResult {
  const now = dayjs();
  const today = now.startOf("day");

  let target = dayjs(hijriToGregorian(hijriYear, hijriMonth, hijriDay)).startOf(
    "day"
  );

  if (target.isBefore(today)) {
    target = dayjs(
      hijriToGregorian(hijriYear + 1, hijriMonth, hijriDay)
    ).startOf("day");
  }

  return buildBreakdown(target, now);
}

/**
 * Computes a full countdown left until a Jalali (Persian solar) date.
 * If that date already passed this Jalali year, rolls forward to next year
 * so recurring annual events always count down to their next occurrence
 * instead of going negative.
 * @param jalaliYear e.g. 1405
 * @param jalaliMonth 1-12
 * @param jalaliDay 1-31
 */
export function getDaysLeftFromJalali(
  jalaliYear: number,
  jalaliMonth: number,
  jalaliDay: number
): DaysLeftResult {
  const now = dayjs();
  const today = now.startOf("day");

  const buildTarget = (year: number) =>
    dayjs().calendar("jalali").year(year).month(jalaliMonth - 1).date(jalaliDay).startOf("day");

  let target = buildTarget(jalaliYear);

  if (target.isBefore(today)) {
    target = buildTarget(jalaliYear + 1);
  }

  return buildBreakdown(target, now);
}

/**
 * Computes a full countdown left until a Gregorian date.
 * If that date already passed this year, rolls forward to next year
 * so recurring annual events always count down to their next occurrence
 * instead of going negative.
 * @param targetDate accepts "YYYY-MM-DD", a Date object, or any dayjs-parsable string
 */
export function getDaysLeftFromGregorian(
  targetDate: string | Date
): DaysLeftResult {
  const now = dayjs();
  const today = now.startOf("day");

  let target = dayjs(targetDate).startOf("day");

  if (target.isBefore(today)) {
    target = target.add(1, "year");
  }

  return buildBreakdown(target, now);
}

/**
 * Returns the current year in Gregorian, Jalali (Persian), and Hijri (Islamic) calendars.
 */
export function getCurrentYears(): CurrentYears {
  const now = new Date();

  const gregorian = now.getFullYear();
  const jalali = dayjs().calendar("jalali").year();

  const hijriYear = new Intl.DateTimeFormat("en-US-u-ca-islamic", {
    year: "numeric",
  })
    .formatToParts(now)
    .find((part) => part.type === "year")?.value;

  return {
    gregorian,
    jalali,
    hijri: Number(hijriYear),
  };
}

// Example usage:
// const result = getDaysLeftFromGregorian("2026-12-31");
// console.log(result.countdown); // "166:04:22:08"
// console.log(result.message);   // "166:04:22:08 left"